import { useState, useRef, useCallback, useEffect } from 'react';
import { MapPin, Mail, Phone, Facebook, Instagram, Twitter, ShieldCheck, CheckCircle2, Lock, ExternalLink, Heart } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/pldfxqlf_logo.png";
const MASCOT_URL = "https://customer-assets.emergentagent.com/job_b35da085-09af-4a55-863c-9eb3e423a8d1/artifacts/erel0x7n_buzon.png";

// Instagram username
const INSTAGRAM_USERNAME = 'tecnoenviosexpress';
const INSTAGRAM_URL = `https://www.instagram.com/${INSTAGRAM_USERNAME}`;

const quickLinks = [
  { name: 'Inicio', href: '#inicio' },
  { name: 'Servicios', href: '#servicios' },
  { name: 'Nosotros', href: '#nosotros' },
  { name: 'FAQ', href: '#faq' },
  { name: 'Contacto', href: '#contacto' },
];

// Orden principal: Venezuela, Colombia, Panamá, República Dominicana + (próximamente) Costa Rica, Nicaragua
const destinations = [
  { name: 'Venezuela', flag: '🇻🇪' },
  { name: 'Colombia', flag: '🇨🇴' },
  { name: 'Panamá', flag: '🇵🇦' },
  { name: 'Rep. Dominicana', flag: '🇩🇴' },
  { name: 'Costa Rica', flag: '🇨🇷', comingSoon: true },
  { name: 'Nicaragua', flag: '🇳🇮', comingSoon: true },
];

const socialLinks = [
  { icon: Facebook, href: '#', label: 'Facebook', color: 'hover:bg-[#1877F2]' },
  { icon: Instagram, href: INSTAGRAM_URL, label: 'Instagram', color: 'hover:bg-gradient-to-tr hover:from-yellow-400 hover:via-pink-500 hover:to-purple-600' },
  { icon: Twitter, href: '#', label: 'Twitter', color: 'hover:bg-[#1DA1F2]' },
];

// Instagram feed images - placeholder images representing courier/shipping content
const instagramPosts = [
  { id: 1, image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=300&h=300&fit=crop', alt: 'Envío de paquetes' },
  { id: 2, image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=300&h=300&fit=crop', alt: 'Logística internacional' },
  { id: 3, image: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=300&h=300&fit=crop', alt: 'Cajas de envío' },
  { id: 4, image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=300&h=300&fit=crop', alt: 'Almacén de paquetes' },
  { id: 5, image: 'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=300&h=300&fit=crop', alt: 'Entrega a domicilio' },
  { id: 6, image: 'https://images.unsplash.com/photo-1580674285054-bed31e145f59?w=300&h=300&fit=crop', alt: 'Paquetería express' },
];

// Instagram Icon SVG
const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// Slider Captcha for Footer (LIGHT THEME)
const FooterSliderCaptcha = ({ onVerified, verified }) => {
  const [position, setPosition] = useState(0);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const threshold = 85;

  const handleMouseDown = (e) => {
    if (verified) return;
    e.preventDefault();
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || verified) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const sliderWidth = 44;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, e.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    setPosition(pct);
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [verified, onVerified]);

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      setPosition(prev => prev >= threshold ? 100 : 0);
    }
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleTouchStart = () => {
    if (verified) return;
    isDragging.current = true;
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || verified) return;
    const container = containerRef.current;
    if (!container) return;
    const touch = e.touches[0];
    const rect = container.getBoundingClientRect();
    const sliderWidth = 44;
    const maxPosition = rect.width - sliderWidth;
    const newPosition = Math.min(Math.max(0, touch.clientX - rect.left - sliderWidth / 2), maxPosition);
    const pct = (newPosition / maxPosition) * 100;
    setPosition(pct);
    if (pct >= threshold) {
      setPosition(100);
      isDragging.current = false;
      onVerified(true);
    }
  };

  const handleTouchEnd = () => {
    if (isDragging.current && position < threshold) {
      setPosition(0);
    }
    isDragging.current = false;
  };

  return (
    <div 
      ref={containerRef}
      className={`relative h-10 rounded-xl border-2 transition-colors overflow-hidden select-none ${
        verified 
          ? 'bg-[#2DD4BF]/10 border-[#2DD4BF]' 
          : 'bg-white border-slate-200 shadow-inner'
      }`}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="footer-slider-captcha"
    >
      <div 
        className={`absolute inset-y-0 left-0 ${
          verified ? 'bg-[#2DD4BF]/20' : 'bg-[#0066CC]/10'
        }`}
        style={{ width: `${position}%`, transition: isDragging.current ? 'none' : 'width 0.2s' }}
      />
      
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {verified ? (
          <span className="text-[#0d9488] font-semibold flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4" />
            Info desbloqueada
          </span>
        ) : (
          <span className="text-slate-500 text-sm flex items-center gap-2 font-medium">
            <Lock className="w-3 h-3" />
            Desliza para ver contacto →
          </span>
        )}
      </div>
      
      <div
        className={`absolute top-1 bottom-1 w-8 rounded-lg flex items-center justify-center shadow-md ${
          verified 
            ? 'bg-[#2DD4BF] cursor-default' 
            : 'bg-gradient-to-br from-[#0066CC] to-[#2DD4BF] hover:shadow-lg cursor-grab active:cursor-grabbing'
        }`}
        style={{ 
          left: `calc(${position}% - ${position * 0.3}px + 4px)`,
          transition: isDragging.current ? 'none' : 'left 0.2s'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid="footer-slider-thumb"
      >
        {verified ? (
          <CheckCircle2 className="w-4 h-4 text-white" />
        ) : (
          <div className="flex gap-0.5">
            <div className="w-0.5 h-3 bg-white/80 rounded-full" />
            <div className="w-0.5 h-3 bg-white/80 rounded-full" />
            <div className="w-0.5 h-3 bg-white/80 rounded-full" />
          </div>
        )}
      </div>
    </div>
  );
};

// Protected contact info (LIGHT THEME)
const ProtectedContactInfo = ({ verified }) => {
  const [contactData, setContactData] = useState(null);
  
  useEffect(() => {
    if (verified && !contactData) {
      const timer = setTimeout(() => {
        const buildPhone = (p1, p2, p3) => {
          return [
            String.fromCharCode(40), p1,
            String.fromCharCode(41), ' ', p2,
            String.fromCharCode(45), p3
          ].join('');
        };
        setContactData({
          address: '8576 NW 56th St, Doral, FL 33166',
          email: 'info@tecnoenviosexpress.com',
          phones: [
            buildPhone('786', '715', '9834'),
            buildPhone('786', '589', '9300')
          ]
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [verified, contactData]);

  if (!verified) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 text-slate-400">
          <MapPin className="w-4 h-4 text-slate-300 flex-shrink-0" />
          <div className="h-3 bg-slate-200 rounded w-32 animate-pulse" />
        </div>
        <div className="flex items-center gap-3 text-slate-400">
          <Mail className="w-4 h-4 text-slate-300 flex-shrink-0" />
          <div className="h-3 bg-slate-200 rounded w-36 animate-pulse" />
        </div>
        <div className="flex items-start gap-3 text-slate-400">
          <Phone className="w-4 h-4 text-slate-300 flex-shrink-0 mt-0.5" />
          <div className="space-y-1.5">
            <div className="h-3 bg-slate-200 rounded w-24 animate-pulse" />
            <div className="h-3 bg-slate-200 rounded w-24 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!contactData) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="w-5 h-5 border-2 border-[#0066CC] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      <li className="flex items-start gap-3 text-slate-600 text-sm">
        <MapPin className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" />
        <span>{contactData.address}</span>
      </li>
      <li className="flex items-center gap-3 text-slate-600 text-sm">
        <Mail className="w-4 h-4 text-[#0066CC] flex-shrink-0" />
        <a 
          href={`mailto:${contactData.email}`} 
          className="hover:text-[#0066CC] transition-colors font-medium"
        >
          {contactData.email}
        </a>
      </li>
      <li className="flex items-start gap-3 text-slate-600 text-sm">
        <Phone className="w-4 h-4 text-[#0066CC] flex-shrink-0 mt-0.5" />
        <div className="flex flex-col gap-0.5 select-none font-medium">
          {contactData.phones.map((phone, idx) => (
            <span key={idx}>{phone}</span>
          ))}
        </div>
      </li>
    </ul>
  );
};

// Instagram Feed Widget (LIGHT THEME)
const InstagramFeed = () => {
  return (
    <div className="mt-10 pt-10 border-t border-slate-200">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-5 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-0.5 shadow-md">
            <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
              <InstagramIcon className="w-5 h-5 bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 bg-clip-text" style={{ color: '#E1306C' }} />
            </div>
          </div>
          <div>
            <p className="text-slate-900 font-bold text-sm">@{INSTAGRAM_USERNAME}</p>
            <p className="text-slate-500 text-xs">Síguenos en Instagram</p>
          </div>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-5 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all"
          data-testid="instagram-follow-btn"
        >
          Seguir
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      
      {/* Photo Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {instagramPosts.map((post) => (
          <a
            key={post.id}
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="relative aspect-square overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow"
            data-testid={`instagram-post-${post.id}`}
          >
            <img
              src={post.image}
              alt={post.alt}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
              <InstagramIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </a>
        ))}
      </div>
      
      {/* View More */}
      <div className="mt-4 text-center">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-500 hover:text-[#0066CC] text-xs transition-colors inline-flex items-center gap-1 font-medium"
        >
          Ver más en Instagram
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export const Footer = () => {
  const [contactVerified, setContactVerified] = useState(false);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-b from-[#F8FAFC] via-white to-[#F0F9FF] text-slate-700 overflow-hidden" data-testid="footer">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#2DD4BF]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-[#0066CC]/10 rounded-full blur-3xl translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-[#F5A623]/10 rounded-full blur-3xl pointer-events-none" />

      {/* Top wave separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#0066CC]/30 to-transparent" />

      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <img 
                src={LOGO_URL} 
                alt="Tecno Envios Express" 
                className="h-11 w-auto"
              />
            </div>
            <p className="text-slate-600 text-sm mb-5 leading-relaxed">
              Tu puente de confianza para envíos internacionales a Latinoamérica. 
              Rápido, seguro y confiable.
            </p>
            {/* Mascot card */}
            <div className="inline-flex items-center gap-3 bg-white rounded-2xl p-3 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <img 
                src={MASCOT_URL} 
                alt="Mascota Buzón" 
                className="w-14 h-14 object-contain"
                data-testid="footer-mascot"
              />
              <div className="pr-2">
                <p className="text-xs font-bold text-slate-900">¡Estamos aquí!</p>
                <p className="text-xs text-slate-500">Listos para enviar</p>
              </div>
            </div>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-base mb-4 text-slate-900 relative inline-block">
              Enlaces
              <span className="absolute -bottom-1 left-0 w-8 h-0.5 bg-[#0066CC] rounded-full" />
            </h4>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.href); }}
                    className="text-slate-600 hover:text-[#0066CC] transition-colors text-sm inline-flex items-center gap-1.5 group"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    <span className="w-1 h-1 bg-[#2DD4BF] rounded-full group-hover:w-3 transition-all" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Destinations */}
          <div>
            <h4 className="font-bold text-base mb-4 text-slate-900 relative inline-block">
              Destinos
              <span className="absolute -bottom-1 left-0 w-8 h-0.5 bg-[#F5A623] rounded-full" />
            </h4>
            <ul className="space-y-2.5">
              {destinations.map((dest) => (
                <li key={dest.name} className="flex items-center gap-2 text-slate-600 text-sm hover:text-slate-900 transition-colors">
                  <span className="text-lg">{dest.flag}</span>
                  <span className="font-medium">{dest.name}</span>
                  {dest.comingSoon && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#F5A623] to-[#CC0000] text-white px-2 py-0.5 rounded-full">
                      Pronto
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact Info - Protected */}
          <div>
            <h4 className="font-bold text-base mb-4 text-slate-900 flex items-center gap-2 relative">
              <span className="relative inline-block">
                Contacto
                <span className="absolute -bottom-1 left-0 w-8 h-0.5 bg-[#2DD4BF] rounded-full" />
              </span>
              {!contactVerified && (
                <Lock className="w-3.5 h-3.5 text-slate-400" />
              )}
              {contactVerified && (
                <CheckCircle2 className="w-3.5 h-3.5 text-[#2DD4BF]" />
              )}
            </h4>
            
            {/* Slider to unlock contact */}
            {!contactVerified && (
              <div className="mb-4">
                <p className="text-slate-500 text-xs mb-2 flex items-center gap-1 font-medium">
                  <ShieldCheck className="w-3 h-3 text-[#2DD4BF]" />
                  Protegido contra bots
                </p>
                <FooterSliderCaptcha 
                  onVerified={setContactVerified}
                  verified={contactVerified}
                />
              </div>
            )}
            
            {/* Contact Info */}
            <ProtectedContactInfo verified={contactVerified} />
            
            {/* Social Links */}
            <div className="flex gap-2.5 mt-5">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.href.startsWith('http') ? '_blank' : undefined}
                  rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className={`w-9 h-9 bg-white border border-slate-200 text-slate-600 hover:text-white rounded-full flex items-center justify-center transition-all hover:scale-110 hover:shadow-md ${social.color}`}
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
        
        {/* Instagram Feed Widget */}
        <InstagramFeed />
      </div>
      
      {/* Bottom Bar */}
      <div className="relative border-t border-slate-200 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-slate-500 text-xs text-center md:text-left flex items-center gap-1.5">
              © {currentYear} Tecno Envios Express. Hecho con
              <Heart className="w-3 h-3 text-[#CC0000] fill-[#CC0000] inline" />
              para Latinoamérica.
            </p>
            <div className="flex gap-5 text-xs">
              <a href="#" className="text-slate-500 hover:text-[#0066CC] transition-colors font-medium">
                Términos y Condiciones
              </a>
              <a href="#" className="text-slate-500 hover:text-[#0066CC] transition-colors font-medium">
                Política de Privacidad
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
